import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../services/api";
import PersonChip from "../components/ui/PersonChip";
import ExportXlsxButton from "../components/ui/ExportXlsxButton";
import OriginIcon from "../components/ui/OriginIcon";
import { ticketOrigin } from "../utils/origin";
import { msPhotoUrl, buildAnalystByName, assignedAvatarSrc } from "../utils/avatars";
import Layout from "../components/Layout";
import { Card, CardHeader, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import SlaBadge from "../components/ui/SlaBadge";
import { Plus, Search } from "lucide-react";
import EditTicketModal from "../components/tickets/EditTicketModal";
import { usePermissions } from "../context/PermissionsContext";
import PaginationBar from "../components/ui/PaginationBar";
import ticketCategoriesService from "../services/ticketCategoriesService";
import { parseApiDateTime } from "../utils/datetime";

const PAGE_SIZE_DEFAULT = 25;

const Tickets = () => {
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Filtro inicial pode vir da URL (?status=Aberto), ex.: cards do Dashboard.
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [analysts, setAnalysts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [hasNextPage, setHasNextPage] = useState(false);

  const navigate = useNavigate();

  // Mapa nome-completo -> analista (assigned_to guarda "first last"), pra achar
  // a foto/e-mail do Responsável a partir do nome exibido no chamado.
  const analystByName = useMemo(() => buildAnalystByName(analysts), [analysts]);

  const { can } = usePermissions();
  // Acoes gated por permissao de tela (Fase 4). Admin/owner fazem bypass.
  const canEdit = can("edit", "/tickets");
  const canCreate = can("create", "/tickets");

  const formatDateTime = (value) => {
    const date = parseApiDateTime(value);
    if (!date) return "-";
    return date.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  };

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, priorityFilter, search, page, viewMode, pageSize]);

  useEffect(() => {
    fetchAnalysts();
    ticketCategoriesService
      .list()
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);

      const params = {
        skip: page * pageSize,
        limit: pageSize + 1,
      };

      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (search) params.search = search;
      // "Minha fila"/"Finalizados" filtram no servidor (antes da paginacao),
      // senao so olhariam a pagina atual (bug #11).
      if (viewMode === "my" || viewMode === "finished") params.view = viewMode;

      const response = await api.get("/tickets/", { params });
      const data = Array.isArray(response.data) ? response.data : [];

      if (data.length > pageSize) {
        setHasNextPage(true);
        setTickets(data.slice(0, pageSize));
      } else {
        setHasNextPage(false);
        setTickets(data);
      }
    } catch (error) {
      console.error("Erro ao buscar chamados:", error);
      setTickets([]);
      setHasNextPage(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalysts = async () => {
    try {
      // Lista de "fila": usuários marcados como atribuíveis (is_assignable).
      const response = await api.get("/users/assignable");
      setAnalysts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao buscar responsáveis (fila):", error);
      setAnalysts([]);
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      Aberto: "bg-blue-100 text-blue-800",
      "Em Andamento": "bg-yellow-100 text-yellow-800",
      "Aguardando aprovação": "bg-cyan-100 text-cyan-800",
      Encerrado: "bg-green-100 text-green-800",
      Cancelado: "bg-red-100 text-red-800",
    };
    return classes[status] || "bg-gray-100 text-gray-800";
  };

  const getPriorityBadgeClass = (priority) => {
    const classes = {
      Urgente: "bg-red-100 text-red-800",
      Alta: "bg-orange-100 text-orange-800",
      Media: "bg-blue-100 text-blue-800",
      Baixa: "bg-gray-100 text-gray-800",
    };
    return classes[priority] || "bg-gray-100 text-gray-800";
  };

  const openEdit = (ticket, e) => {
    if (e) e.stopPropagation();
    setSelectedTicket(ticket);
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setSelectedTicket(null);
    setIsEditOpen(false);
  };

  const handleEditSubmit = async (formData) => {
    if (!selectedTicket) return;

    try {
      await api.put(`/tickets/${selectedTicket.id}`, {
        status: formData.status,
        priority: formData.priority,
        category: formData.category,
        assigned_to: formData.assigned_to,
      });

      if (formData.comment && formData.comment.trim()) {
        await api.post(`/tickets/${selectedTicket.id}/comments`, {
          comment: formData.comment.trim(),
        });
      }

      closeEdit();
      await fetchTickets();
    } catch (error) {
      console.error("Erro ao atualizar chamado:", error.response?.data || error);
      alert("Erro ao atualizar chamado.");
    }
  };

  // Filtros (busca/status/prioridade/modo) sao aplicados no SERVIDOR antes da
  // paginacao, entao a lista ja vem pronta — nao re-filtra no cliente (corrige
  // o bug #11 de "Minha fila"/"Finalizados" verem so a pagina atual).
  const filteredTickets = tickets;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Chamados</h1>
            <p className="text-gray-500">Gerencie todos os chamados</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {canEdit && (
              <div className="inline-flex rounded-md border bg-white text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setViewMode("all");
                    setPage(0);
                  }}
                  className={`px-3 py-1.5 rounded-l-md ${
                    viewMode === "all"
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode("my");
                    setPage(0);
                  }}
                  className={`px-3 py-1.5 ${
                    viewMode === "my"
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Minha fila
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode("finished");
                    setPage(0);
                  }}
                  className={`px-3 py-1.5 rounded-r-md ${
                    viewMode === "finished"
                      ? "bg-gray-800 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Chamados Finalizados
                </button>
              </div>
            )}

            <ExportXlsxButton url="/tickets/export" filename="chamados.xlsx" />

            {canCreate && (
              <Button onClick={() => navigate("/tickets/new")}>
                <Plus className="w-4 h-4 mr-2" />
                Novo Chamado
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    placeholder="Buscar chamados..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(0);
                    }}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos os Status</option>
                  <option value="Aberto">Aberto</option>
                  <option value="Em Andamento">Em Andamento</option>
                  <option value="Encerrado">Encerrado</option>
                  <option value="Cancelado">Cancelado</option>
                </select>

                <select
                  value={priorityFilter}
                  onChange={(e) => {
                    setPriorityFilter(e.target.value);
                    setPage(0);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todas as Prioridades</option>
                  <option value="Urgente">Urgente</option>
                  <option value="Alta">Alta</option>
                  <option value="Media">Média</option>
                  <option value="Baixa">Baixa</option>
                </select>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : filteredTickets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhum chamado encontrado
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-center py-3 px-2 text-sm font-semibold text-gray-600" title="Origem: robô = automático, pessoa = manual">Origem</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">ID</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Título</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Solicitante</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Responsável</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Prioridade</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Criado em</th>
                        {canEdit && (
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Ações</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTickets.map((ticket) => (
                        <tr
                          key={ticket.id}
                          onClick={() => navigate(`/tickets/${ticket.id}`)}
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        >
                          <td className="py-3 px-2">
                            <div className="flex justify-center">
                              <OriginIcon origin={ticketOrigin(ticket)} size={26} />
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900">#{ticket.id}</td>
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">{ticket.title}</td>
                          <td className="py-3 px-4">
                            <PersonChip
                              name={ticket.requester_name}
                              src={msPhotoUrl(ticket.requester_email, ticket.requester_name)}
                              placeholder="N/A"
                            />
                          </td>
                          <td className="py-3 px-4">
                            <PersonChip
                              name={ticket.assigned_to}
                              src={assignedAvatarSrc(ticket.assigned_to, analystByName)}
                              placeholder="—"
                            />
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-col items-start gap-1">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(ticket.status)}`}>
                                {ticket.status}
                              </span>
                              <SlaBadge status={ticket.sla_status} />
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getPriorityBadgeClass(ticket.priority)}`}>
                              {ticket.priority}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{formatDateTime(ticket.created_at)}</td>
                          {canEdit && (
                            <td className="py-3 px-4 text-right text-sm">
                              <Button
                                variant="outline"
                                onClick={(e) => openEdit(ticket, e)}
                                className="h-auto px-3 py-1 text-xs text-gray-700"
                              >
                                Editar
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <PaginationBar
                  page={page}
                  pageSize={pageSize}
                  hasNext={hasNextPage}
                  onPageChange={setPage}
                  onPageSizeChange={(s) => {
                    setPageSize(s);
                    setPage(0);
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <EditTicketModal
        isOpen={isEditOpen}
        onClose={closeEdit}
        ticket={selectedTicket}
        onSubmit={handleEditSubmit}
        analysts={analysts}
        categories={categories}
      />
    </Layout>
  );
};

export default Tickets;